import { AdminDocsService } from "../../services/admin-docs.service.js";
import { templateTranslationService as TranslationService } from "../../services/TranslationService.js";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore"; // Needed only for direct menu manipulation if keeping hybrid approach

export const AdminDocsPage = () => {
    const container = document.createElement('div');
    container.className = "flex h-full bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200";

    // --- Template Structure ---
    container.innerHTML = `
        <!-- Sidebar: Document List -->
        <div class="w-64 border-r bg-gray-50 flex flex-col">
            <div class="p-4 border-b bg-white">
                <button id="btn-new-doc" class="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm">
                    <span class="material-symbols-outlined text-[18px]">add</span>
                    새 문서 만들기
                </button>
            </div>
            
            <div id="doc-list" class="flex-1 overflow-y-auto p-2 space-y-1">
                <!-- Doc Items Injected Here -->
                <div class="text-center py-10 text-gray-400">
                    <span class="loader-spin inline-block w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full"></span>
                </div>
            </div>
        </div>

        <!-- Main: Editor Area -->
        <div class="flex-1 flex flex-col min-w-0 bg-white">
            <!-- Toolbar -->
            <div class="h-14 border-b flex items-center justify-between px-6 bg-white shrink-0">
                <div class="flex items-center gap-3">
                    <div id="doc-icon-preview" class="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                        <span class="material-symbols-outlined">description</span>
                    </div>
                    <div>
                        <input type="text" id="doc-title-input" class="text-lg font-bold text-gray-800 placeholder-gray-400 outline-none bg-transparent w-full" placeholder="문서 제목을 입력하세요">
                        <div id="doc-id-display" class="text-[10px] text-gray-400 font-mono -mt-1 hidden"></div>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <span id="save-status" class="text-xs text-green-600 font-medium opacity-0 transition-opacity">저장됨</span>
                    
                    <label class="flex items-center gap-2 mr-2 ml-4 text-xs font-medium text-gray-500 cursor-pointer hover:text-blue-600 transition-colors select-none group" title="저장 시 9개 국어로 자동 번역하여 저장합니다.\n(API 비용 발생 가능, 약간의 시간 소요)">
                        <input type="checkbox" id="chk-auto-translate" class="accent-blue-600 w-3.5 h-3.5 rounded border-gray-300">
                        <span class="group-hover:underline">자동 번역 생성</span>
                        <span class="material-symbols-outlined text-[14px] text-gray-400">translate</span>
                    </label>
                    
                    <button id="btn-add-menu" class="hidden text-gray-500 hover:text-blue-600 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-all flex items-center gap-1">
                        <span class="material-symbols-outlined text-[16px]">menu_open</span>
                        메뉴에 등록
                    </button>

                    <div class="h-4 w-px bg-gray-200 mx-2"></div>

                    <button id="btn-delete" class="text-red-400 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1">
                        <span class="material-symbols-outlined text-[16px]">delete</span>
                        삭제
                    </button>
                    <button id="btn-save" class="bg-gray-900 hover:bg-black text-white px-4 py-1.5 rounded-lg text-sm font-bold shadow-sm transition-transform active:scale-95 flex items-center gap-2">
                        <span class="material-symbols-outlined text-[18px]">save</span>
                        저장
                    </button>
                </div>
            </div>

            <!-- Editor & Preview Split -->
            <div class="flex-1 flex min-h-0">
                <!-- Markdown Input -->
                <div class="flex-1 flex flex-col border-r relative group">
                    <div class="absolute top-2 right-2 text-[10px] items-center gap-1 text-gray-400 bg-white/80 px-2 py-1 rounded backdrop-blur border hidden group-hover:flex pointer-events-none">
                        <span class="material-symbols-outlined text-[12px]">markdown</span> Markdown
                    </div>
                    <textarea id="doc-content-input" class="flex-1 w-full h-full p-6 resize-none outline-none font-mono text-sm leading-relaxed text-gray-700 selection:bg-blue-100" placeholder="# 내용을 입력하세요..."></textarea>
                </div>

                <!-- Live Preview -->
                <div class="flex-1 flex flex-col bg-gray-50/50">
                    <div class="flex-1 overflow-y-auto p-8 custom-scrollbar">
                        <div id="doc-preview" class="prose prose-sm max-w-none prose-headings:font-bold prose-a:text-blue-600">
                             <!-- Preview Rendered Here -->
                             <div class="flex flex-col items-center justify-center h-full text-gray-300 select-none">
                                <span class="material-symbols-outlined text-4xl mb-2">preview</span>
                                <p>미리보기 영역</p>
                             </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    // --- DOM Elements ---
    const btnNew = container.querySelector('#btn-new-doc');
    const docListEl = container.querySelector('#doc-list');
    const chkAutoTranslate = container.querySelector('#chk-auto-translate');

    const titleInput = container.querySelector('#doc-title-input');
    const idDisplay = container.querySelector('#doc-id-display');
    const contentInput = container.querySelector('#doc-content-input');
    const previewEl = container.querySelector('#doc-preview');

    const btnSave = container.querySelector('#btn-save');
    const btnDelete = container.querySelector('#btn-delete');
    const btnAddMenu = container.querySelector('#btn-add-menu');
    const saveStatus = container.querySelector('#save-status');

    let currentDocId = null;
    let isSystemDoc = false;
    let unsubscribeDocs = null;
    const docsMap = new Map();

    // --- Event Handlers & Logic ---

    // 1. Load Document to Editor
    const loadDoc = (docId) => {
        const data = docsMap.get(docId);
        if (!data) return;

        currentDocId = docId;
        isSystemDoc = !!data.isSystem;

        titleInput.value = data.title || '';
        contentInput.value = data.content || '';
        idDisplay.textContent = `ID: ${docId}`;
        idDisplay.classList.remove('hidden');

        // Update active state in list
        renderDocList();

        // Initial Preview Render
        updatePreview(data.content || '');

        // Toggle Buttons
        btnDelete.style.display = isSystemDoc ? 'none' : 'flex';
        btnAddMenu.classList.remove('hidden');
    };

    // 2. Render List
    const renderDocList = () => {
        docListEl.innerHTML = '';
        docsMap.forEach((data) => {
            const btn = document.createElement('button');
            const isActive = currentDocId === data.id;

            btn.className = `w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-3 group ${isActive ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 font-bold' : 'hover:bg-gray-100 text-gray-600'}`;
            btn.innerHTML = `
                <div class="w-8 h-8 rounded-full ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600'} flex items-center justify-center shrink-0 transition-colors">
                    <span class="material-symbols-outlined text-[18px]">${data.isSystem ? 'verified' : 'description'}</span>
                </div>
                <div class="flex-1 min-w-0">
                    <div class="truncate">${data.title || data.id}</div>
                    <div class="text-[10px] text-gray-400 truncate font-mono">${data.id}</div>
                </div>
            `;
            btn.onclick = () => loadDoc(data.id);
            docListEl.appendChild(btn);
        });
    };

    // 3. New Document
    btnNew.onclick = () => {
        currentDocId = null;
        isSystemDoc = false;
        titleInput.value = '';
        contentInput.value = '';
        titleInput.focus();
        idDisplay.classList.add('hidden');
        previewEl.innerHTML = '';

        btnAddMenu.classList.add('hidden');
        btnDelete.style.display = 'flex';

        // Reset list selection
        renderDocList();
    };

    // 4. Save
    btnSave.onclick = async () => {
        const title = titleInput.value.trim();
        const content = contentInput.value;

        if (!title) {
            alert('제목을 입력해주세요.');
            return;
        }

        try {
            btnSave.disabled = true;
            btnSave.innerHTML = `<span class="loader-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></span>`;

            // Generate ID if new
            if (!currentDocId) {
                currentDocId = 'doc_' + Date.now();
            }

            // [NEW] Intelligent Auto-Translation Logic
            let translations = null;
            if (chkAutoTranslate.checked) {
                const targetLocales = ['en', 'ja', 'zh', 'vi', 'th', 'fr', 'de', 'ru', 'es'];
                translations = {};

                // Show progressive feedback (Hack button text)
                const originalBtnText = btnSave.innerHTML;

                // Process in parallel (batches of 3 ideally, but 9 is low enough for Promise.all)
                // However, let's do Promise.all for speed.
                await Promise.all(targetLocales.map(async (lang) => {
                    try {
                        // Parallel fetch
                        const [trTitle, trContent] = await Promise.all([
                            TranslationService.translate(title, lang),
                            TranslationService.translate(content, lang)
                        ]);
                        translations[lang] = { title: trTitle, content: trContent };
                    } catch (err) {
                        console.warn(`Translation failed for ${lang}`, err);
                        // Skip failed lang, others proceed
                    }
                }));
            }

            await AdminDocsService.saveDoc(currentDocId, title, content, isSystemDoc, translations);

            // UI Feedback
            saveStatus.style.opacity = '1';
            setTimeout(() => { saveStatus.style.opacity = '0'; }, 2000);

            btnAddMenu.classList.remove('hidden'); // Enable menu add after save

        } catch (e) {
            console.error(e);
            alert('저장 실패: ' + e.message);
        } finally {
            btnSave.disabled = false;
            btnSave.innerHTML = `<span class="material-symbols-outlined text-[18px]">save</span> 저장`;
        }
    };

    // 5. Delete
    btnDelete.onclick = async () => {
        if (!currentDocId) return;
        if (confirm('정말로 이 문서를 삭제하시겠습니까?')) {
            try {
                await AdminDocsService.deleteDoc(currentDocId);
                btnNew.click(); // Reset to new
            } catch (e) {
                alert('삭제 실패: ' + e.message);
            }
        }
    };

    // 6. Preview Update
    contentInput.oninput = (e) => {
        updatePreview(e.target.value);
    };

    const updatePreview = async (text) => {
        if (!text) {
            previewEl.innerHTML = '<div class="text-gray-300 text-center mt-10">내용 없음</div>';
            return;
        }
        try {
            // Lazy load markdown parser
            const { parseMarkdown } = await import('../../utils/markdown.js');
            previewEl.innerHTML = parseMarkdown(text);
        } catch (e) {
            previewEl.textContent = "Preview Error";
        }
    };

    // 7. Add to Menu (Quick Action)
    btnAddMenu.onclick = async () => {
        if (!currentDocId) return;
        const confirmMsg = `이 문서(${titleInput.value})를 메뉴에 추가하시겠습니까?\n'외부 링크/메뉴 관리' 탭으로 이동하여 설정을 확인하세요.`;
        if (confirm(confirmMsg)) {
            try {
                // Dynamically import Firestore from here to strictly isolate this feature
                const { getFirestore, doc, getDoc, updateDoc } = await import("firebase/firestore");
                const { app } = await import("../../firebase-config.js");
                const db = getFirestore(app);

                const navRef = doc(db, 'app_settings', 'navigation');
                const navSnap = await getDoc(navRef);

                if (navSnap.exists()) {
                    const data = navSnap.data();
                    const items = data.items || [];

                    // Check duplicate
                    if (items.find(i => i.docId === currentDocId)) {
                        alert("이미 메뉴에 등록된 문서입니다.");
                        return;
                    }

                    const newItem = {
                        id: currentDocId,
                        label: titleInput.value,
                        type: 'MODAL',
                        component: 'InfoModal', // Force InfoModal
                        docId: currentDocId,
                        icon: 'FileText', // Default
                        isVisible: true
                    };

                    items.push(newItem);
                    await updateDoc(navRef, { items });
                    alert("메뉴에 성공적으로 추가되었습니다! \n위치 이동은 '외부 링크/메뉴 관리' 탭에서 가능합니다.");
                }

            } catch (e) {
                alert("메뉴 추가 실패: " + e.message);
            }
        }
    };

    // --- Init ---
    (async () => {
        // Run Migration Check silently
        await AdminDocsService.ensureDefaultDocs();

        // Subscribe
        unsubscribeDocs = AdminDocsService.subscribeDocs((docs) => {
            docsMap.clear();
            docs.forEach(d => docsMap.set(d.id, d));
            renderDocList();

            // If editing, re-load to update title changes if any
            if (currentDocId && docsMap.has(currentDocId)) {
                // Ideally refresh meta but not content to prevent overwrite while typing
            } else if (currentDocId && !docsMap.has(currentDocId)) {
                // Deleted remotely
                btnNew.click();
            }
        });
    })();

    return container;
};
